import {
  PdfGen,
  PdfUnit,
  PdfSize,
  PdfRect,
  PdfPoint,
  PdfFont,
  PdfBrush,
  PdfColor,
  ETextAlign,
  PageGen,
} from '../../pdf/pdf';
import { ILayoutLabelTextGen } from '../layoutLabelText';
import { OptsLabelText } from '../optsLabelText';
import { GUtil } from '../../pdf/gutil';
import { IItemCount } from '@educraft/base';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';
import '../../pdf/draw/image';
import '../../pdf/draw/label';
import '../../pdf/draw/cut';

/**
 * Inicialy__4 - Word Builder with letter tiles
 *
 * Layout: Multiple portrait pages, 2×5 grid per page.
 * Each cell: Image on left, word shown as individual letter tiles on the right.
 * Each letter of the word is displayed in its own colored square tile.
 *
 * Creative direction: Word as building blocks - each letter is a tangible tile.
 */
export class Inicialy__4 implements ILayoutLabelTextGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 1;
  }
  countMax(): number {
    return 50;
  }

  public async generate(pdf: PdfGen, opts: OptsLabelText): Promise<void> {
    const itemsPerPage = 10;
    const pagesNeeded = Math.ceil(opts.list.length / itemsPerPage);
    const pages: PageGen[] = [];
    for (let i = 0; i < pagesNeeded; i++) {
      pages.push(pdf.addPage());
    }

    const margin = PdfUnit.FromMillimeters(5);
    const cellWidth = PdfSize.A4.width / 2;
    const cellHeight = PdfSize.A4.height / 5;

    const tilePalettes: string[][] = [
      [
        '#FF6B6B',
        '#FF8E53',
        '#FFA502',
        '#FFD93D',
        '#6BCB77',
        '#4D96FF',
        '#9B59B6',
        '#E91E63',
        '#00BCD4',
        '#FF5722',
      ],
      [
        '#A8E6CF',
        '#DCEDC1',
        '#FFD3B6',
        '#FFAAA5',
        '#FF8B94',
        '#FFEAA7',
        '#DDA0DD',
        '#98D8C8',
        '#F7DC6F',
        '#BB8FCE',
      ],
      [
        '#85C1E9',
        '#F8B500',
        '#E74C3C',
        '#27AE60',
        '#9B59B6',
        '#3498DB',
        '#E67E22',
        '#1ABC9C',
        '#F39C12',
        '#8E44AD',
      ],
    ];

    const tileColors = GUtil.randomBrushes(opts.data.random, tilePalettes);
    const letterFont = new PdfFont(PdfFont.BOLD_FONT_NAME, 24);

    for (let pageIndex = 0; pageIndex < pagesNeeded; pageIndex++) {
      const page = pages[pageIndex];
      const pageStartIndex = pageIndex * itemsPerPage;

      for (let x = 0; x < 2; x++) {
        for (let y = 0; y < 5; y++) {
          const index = pageStartIndex + x * 5 + y;
          if (index >= opts.list.length) continue;

          const localIndex = x * 5 + y;
          const rect = new PdfRect(x * cellWidth, y * cellHeight, cellWidth, cellHeight);

          const imageAreaWidth = rect.width * 0.4;
          const wordAreaWidth = rect.width * 0.6;

          const imageRect = new PdfRect(rect.x, rect.y, imageAreaWidth, rect.height);
          const wordAreaRect = new PdfRect(
            rect.x + imageAreaWidth,
            rect.y,
            wordAreaWidth,
            rect.height
          );

          page.drawLabel(opts.list[index].label, { rect: imageRect });

          const word = opts.list[index].text.toUpperCase().replace(/\s/g, '');
          const letters = word.split('');

          const tilePadding = PdfUnit.FromMillimeters(2);
          const tileSize = Math.min(
            (wordAreaRect.width - tilePadding * 2) / letters.length - tilePadding,
            wordAreaRect.height - tilePadding * 2
          );
          const totalTilesWidth = letters.length * (tileSize + tilePadding) + tilePadding;
          const startX = wordAreaRect.x + (wordAreaRect.width - totalTilesWidth) / 2;
          const startY = wordAreaRect.y + (wordAreaRect.height - tileSize) / 2;

          for (let li = 0; li < letters.length; li++) {
            const tileX = startX + li * (tileSize + tilePadding);
            const tileRect = new PdfRect(tileX, startY, tileSize, tileSize);
            const tileColorIndex = (localIndex + li) % tileColors.length;

            page.drawRectangle(null, tileColors[tileColorIndex], tileRect);
            page.drawString(
              letters[li],
              letterFont,
              PdfBrush.White,
              null,
              tileRect,
              ETextAlign.Center
            );
          }

          page.drawRectangle(GUtil.cutPen, null, rect);
        }
      }
    }
  }
}
